import { useCallback, useEffect, useState } from 'react';
import { supabase, type Student } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import StudentManager from './StudentManager';
import PortfolioView from './PortfolioView';
import AcademySettings from './AcademySettings';
import OwnerStudentApproval from './OwnerStudentApproval';
import CapacityStatus from './CapacityStatus';
import SubscriptionPanel from './SubscriptionPanel';
import SubscriptionHistory from './SubscriptionHistory';

export default function OwnerDashboard() {
  const { profile, refreshProfile } = useAuth();
  const academyId = profile?.academy_id ?? null;
  const [students, setStudents] = useState<Student[]>([]);
  const [maxStudents, setMaxStudents] = useState(100);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadMaxStudents = useCallback(async () => {
    if (!academyId) return;
    try {
      const { data, error: err } = await supabase
        .from('academies')
        .select('max_students')
        .eq('id', academyId)
        .maybeSingle();
      if (err) throw err;
      if (data?.max_students) setMaxStudents(data.max_students);
    } catch (e) {
      console.error(e);
    }
  }, [academyId]);

  const loadStudents = useCallback(async () => {
    setError('');
    try {
      if (!academyId) {
        setStudents([]);
        setSelectedId(null);
        return;
      }
      const { data, error: err } = await supabase
        .from('students')
        .select('*')
        .eq('academy_id', academyId)
        .order('created_at', { ascending: false });
      if (err) throw err;
      const list = (data as Student[]) || [];
      setStudents(list);
      // 첫 학생 자동 선택 제거: 선택된 학생이 목록에서 사라졌을 때만 해제
      setSelectedId((prev) => (prev && list.some((s) => s.id === prev) ? prev : null));
    } catch (e) {
      setError('학생 목록을 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [academyId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    loadMaxStudents();
  }, [loadMaxStudents]);

  const selectedStudent = students.find((s) => s.id === selectedId) || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="mb-6 space-y-4 print:hidden" data-print="hide">
        {profile && <SubscriptionPanel profile={profile} onChanged={refreshProfile} />}
        {profile && <SubscriptionHistory userId={profile.id} />}
        <AcademySettings />
        {academyId && <OwnerStudentApproval academyId={academyId} />}
        {academyId && (
          <CapacityStatus
            academyId={academyId}
            studentCount={students.length}
            maxStudents={maxStudents}
            onMaxChange={loadMaxStudents}
          />
        )}
      </div>

      <div className="print:block">
        {selectedStudent ? (
          <div>
            <div className="mb-4 print:hidden">
              <button
                onClick={() => setSelectedId(null)}
                className="whitespace-nowrap inline-flex items-center gap-2 text-sm font-semibold text-foreground-600 bg-white border border-background-200 rounded-md px-4 py-2 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                학생 목록으로
              </button>
            </div>
            <PortfolioView student={selectedStudent} />
          </div>
        ) : (
          <StudentManager
            students={students}
            onStudentsChange={loadStudents}
            selectedId={selectedId}
            onSelect={setSelectedId}
            maxStudents={maxStudents}
          />
        )}
      </div>
    </div>
  );
}